import React, { useEffect, useState } from 'react';
import { Send, KeyRound, Calendar, Phone, BarChart3, ImageIcon, Search, ExternalLink, Save, Loader2, Plane } from 'lucide-react';
import { adminApi } from './api';

function SettingRow({ icon: Icon, title, desc, status, children, testid }) {
  return (
    <div className="dark-card" style={{ padding: 22 }} data-testid={testid}>
      <div className="flex items-start gap-3">
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'rgba(197, 165, 114, 0.10)', display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <Icon size={16} style={{ color: 'var(--gold)' }} strokeWidth={1.7} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="dark-h2" style={{ fontSize: 18 }}>{title}</div>
            {status === 'soon' && <span className="dark-tag">скоро</span>}
            {status === 'ready' && <span className="dark-tag new">готово к подключению</span>}
            {status === 'live' && <span className="dark-tag new">работает</span>}
          </div>
          <p className="dark-meta" style={{ marginTop: 6, lineHeight: 1.6 }}>{desc}</p>
          {children && <div style={{ marginTop: 14 }}>{children}</div>}
        </div>
      </div>
    </div>
  );
}

function InfoBox({ children, testid }) {
  return (
    <div
      style={{
        marginTop: 10,
        padding: '12px 14px',
        background: 'rgba(197, 165, 114, 0.06)',
        border: '1px solid rgba(197, 165, 114, 0.18)',
        borderRadius: 10,
        fontSize: 12,
        color: 'var(--cream-mute)',
        lineHeight: 1.6,
      }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

export default function AdminSettings() {
  // Текущие значения подгруженных счётчиков (для отображения «подключено / нет»)
  const ymId = process.env.REACT_APP_YANDEX_METRIKA_ID || '';
  const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} data-testid="admin-settings">
      <div className="dark-card" style={{ padding: 28 }}>
        <div className="dark-eyebrow">Кабинет</div>
        <h1 className="dark-h1" style={{ fontSize: 42, marginTop: 6 }}>Настройки</h1>
        <p className="dark-meta" style={{ marginTop: 10, maxWidth: 560, lineHeight: 1.6 }}>
          Telegram-бот для заявок, расписание приёма, контакты и аналитика. По мере готовности —
          мы будем включать разделы по одному, чтобы вы не запутались.
        </p>
      </div>

      {/* Telegram */}
      <SettingRow
        icon={Send}
        title="Telegram-бот"
        desc="Заявки и сводки разговоров с Аней приходят прямо в Telegram. Нужны токен бота и ваш chat_id."
        status="ready"
        testid="settings-telegram"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 520 }}>
          <input className="dark-input" placeholder="TELEGRAM_BOT_TOKEN" disabled data-testid="settings-tg-token" />
          <input className="dark-input" placeholder="TELEGRAM_CHAT_ID" disabled data-testid="settings-tg-chat" />
          <InfoBox testid="settings-tg-info">
            Чтобы получить токен: откройте в Telegram <strong>@BotFather</strong> → «/newbot» → дайте имя →
            он пришлёт строку вида <code style={{ color: 'var(--gold)' }}>1234567:AAHk-…</code>.<br />
            Чтобы получить chat_id: напишите своему боту любое сообщение, потом откройте
            <code style={{ color: 'var(--gold)' }}> https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> —
            ваш chat_id будет в поле <code>chat.id</code>.
          </InfoBox>
        </div>
      </SettingRow>

      {/* Яндекс.Метрика */}
      <SettingRow
        icon={BarChart3}
        title="Яндекс.Метрика"
        desc="Подключение счётчика Яндекс.Метрики — увидите посетителей сайта, источники, поведение, тепловую карту."
        status={ymId ? 'live' : 'ready'}
        testid="settings-yandex"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 520 }}>
          <input
            className="dark-input"
            placeholder="например, 87654321"
            value={ymId}
            disabled
            data-testid="settings-ym-id"
          />
          <InfoBox testid="settings-ym-info">
            Создайте счётчик на <a href="https://metrika.yandex.by/" target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--gold)', textDecoration: 'underline' }}>metrika.yandex.by</a>,
            скопируйте номер счётчика (8 цифр) и пришлите — мы вставим его в переменную
            <code style={{ color: 'var(--gold)' }}> REACT_APP_YANDEX_METRIKA_ID</code> и пересоберём сайт.
            На странице индексации появятся: посещения, отказы, тепловая карта кликов и Вебвизор.
          </InfoBox>
        </div>
      </SettingRow>

      {/* Google Analytics */}
      <SettingRow
        icon={Search}
        title="Google Analytics 4"
        desc="Подключение GA4 — нужно если Светлана работает с гугл-рекламой или интересна аналитика по миру."
        status={gaId ? 'live' : 'ready'}
        testid="settings-google"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 520 }}>
          <input
            className="dark-input"
            placeholder="например, G-XXXXXXXXXX"
            value={gaId}
            disabled
            data-testid="settings-ga-id"
          />
          <InfoBox testid="settings-ga-info">
            Создайте ресурс на <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--gold)', textDecoration: 'underline' }}>analytics.google.com</a>,
            возьмите Measurement ID (формат <code style={{ color: 'var(--gold)' }}>G-XXXXXXXXXX</code>) и
            пришлите — впишем в <code style={{ color: 'var(--gold)' }}>REACT_APP_GA_MEASUREMENT_ID</code>.
            IP-адреса автоматически анонимизированы.
          </InfoBox>
        </div>
      </SettingRow>

      {/* SEO и соцсети */}
      <SettingRow
        icon={ExternalLink}
        title="SEO и репост в соцсети"
        desc="Сайт уже размечен по Schema.org (Person + ProfessionalService), Open Graph, Twitter Cards и для Telegram-instant-view. Превью при репосте корректное."
        status="live"
        testid="settings-seo"
      >
        <InfoBox testid="settings-seo-info">
          Что уже подключено:<br />
          • <strong>Schema.org JSON-LD</strong> — Google понимает, что Светлана психолог в Минске, видит услуги и цены<br />
          • <strong>Open Graph</strong> — корректные превью в Facebook, VK, LinkedIn, Telegram, WhatsApp<br />
          • <strong>Twitter Cards</strong> (summary_large_image) — большая карточка с фото в X/Twitter<br />
          • <strong>robots.txt + sitemap.xml</strong> — поисковики видят страницы, админка закрыта от индексации<br />
          • <strong>Favicon</strong> — SVG (винный с золотом) для всех браузеров и устройств<br />
          • <strong>Apple touch icon</strong> + PWA manifest — иконка при добавлении на главный экран iPhone/Android<br />
          • <strong>Геопривязка</strong> — region BY-MI, geo placename Минск — для локального поиска
        </InfoBox>
      </SettingRow>

      <ScheduleEditor />

      <SettingRow
        icon={Phone}
        title="Контакты в подвале сайта"
        desc="Телефон, email, Telegram, адрес кабинета — то, что видят посетители в самом низу страницы."
        status="soon"
        testid="settings-contacts"
      />

      <SettingRow
        icon={ImageIcon}
        title="Фото в шапке"
        desc="Главное фото Светланы на первом экране. Можно заменить, не трогая остальное."
        status="soon"
        testid="settings-photo"
      />

      <SettingRow
        icon={KeyRound}
        title="Пароль входа в кабинет"
        desc="Если хотите сменить пароль — сделаем вместе. Сейчас защита достаточная для одного владельца."
        status="soon"
        testid="settings-password"
      />
    </div>
  );
}


// ===========================================================================
// Редактор расписания приёма (плоский, простой)
// ===========================================================================
function ScheduleEditor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let stopped = false;
    adminApi.scheduleGet()
      .then((d) => { if (!stopped) { setData(d); setLoading(false); } })
      .catch((e) => { if (!stopped) { setError(e.message || 'Не удалось загрузить'); setLoading(false); } });
    return () => { stopped = true; };
  }, []);

  const setDay = (i, key, value) => {
    setData((d) => {
      const days = [...(d.days || [])];
      days[i] = { ...days[i], [key]: value };
      return { ...d, days };
    });
  };
  const setVac = (key, value) => {
    setData((d) => ({ ...d, vacation: { ...(d.vacation || {}), [key]: value } }));
  };

  const onSave = async () => {
    setSaving(true);
    setError('');
    try {
      const updated = await adminApi.scheduleUpdate({ days: data.days, vacation: data.vacation });
      setData(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      setError(e.message || 'Не сохранилось');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dark-card" style={{ padding: 22 }} data-testid="settings-schedule-loading">
        <Loader2 size={16} className="animate-spin" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="dark-card" style={{ padding: 22 }} data-testid="settings-schedule-error">
        {error || 'Расписание недоступно'}
      </div>
    );
  }

  const days = data.days || [];
  const vac = data.vacation || {};

  return (
    <div className="dark-card" style={{ padding: 22 }} data-testid="settings-schedule">
      <div className="flex items-start gap-3">
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'rgba(197, 165, 114, 0.10)', display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <Calendar size={16} style={{ color: 'var(--gold)' }} strokeWidth={1.7} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="dark-h2" style={{ fontSize: 18 }}>Расписание приёма</div>
            <span className="dark-tag new">работает</span>
          </div>
          <p className="dark-meta" style={{ marginTop: 6, lineHeight: 1.6 }}>
            Дни недели и часы. Пустое поле часов = выходной, тогда работает текст-подпись.
            Виджет «Когда с вами» на публичном сайте берёт эти данные.
          </p>

          {/* Дни недели */}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 620 }}>
            {days.map((d, i) => (
              <div
                key={d.key}
                style={{ display: 'grid', gridTemplateColumns: '46px 1fr 1fr', gap: 8, alignItems: 'center' }}
                data-testid={`schedule-day-${d.key}`}
              >
                <div style={{ color: 'var(--cream-soft)', fontSize: 13 }}>{d.label}</div>
                <input
                  className="dark-input"
                  placeholder="10:00–19:00"
                  value={d.hours}
                  onChange={(e) => setDay(i, 'hours', e.target.value)}
                  data-testid={`schedule-hours-${d.key}`}
                />
                <input
                  className="dark-input"
                  placeholder="если пусто — подпись (напр. «по договорённости»)"
                  value={d.note}
                  onChange={(e) => setDay(i, 'note', e.target.value)}
                  data-testid={`schedule-note-${d.key}`}
                />
              </div>
            ))}
          </div>

          {/* Отпуск */}
          <div
            style={{
              marginTop: 20,
              padding: 14,
              border: '1px solid rgba(148, 0, 74, 0.24)',
              background: 'rgba(148, 0, 74, 0.05)',
              borderRadius: 8,
            }}
            data-testid="schedule-vacation"
          >
            <label className="flex items-center gap-3" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!vac.enabled}
                onChange={(e) => setVac('enabled', e.target.checked)}
                data-testid="schedule-vacation-enabled"
                style={{ width: 16, height: 16, accentColor: 'var(--wine-bright)' }}
              />
              <Plane size={14} style={{ color: 'var(--wine-bright)' }} />
              <span style={{ color: 'var(--cream-soft)', fontSize: 14 }}>Отпуск — показать баннер над сайтом</span>
            </label>
            {vac.enabled && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 520 }}>
                <input
                  className="dark-input"
                  placeholder="До какой даты (например: 15 сентября)"
                  value={vac.until || ''}
                  onChange={(e) => setVac('until', e.target.value)}
                  data-testid="schedule-vacation-until"
                />
                <input
                  className="dark-input"
                  placeholder="Текст под датой (что услышит посетитель)"
                  value={vac.note || ''}
                  onChange={(e) => setVac('note', e.target.value)}
                  data-testid="schedule-vacation-note"
                />
              </div>
            )}
          </div>

          {/* Save */}
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={onSave}
              disabled={saving}
              className="dark-btn dark-btn-primary"
              style={{ padding: '9px 16px' }}
              data-testid="schedule-save"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving ? 'Сохраняю…' : 'Сохранить'}
            </button>
            {saved && <span style={{ color: 'var(--gold)', fontSize: 12 }} data-testid="schedule-saved">Сохранено — публичный сайт обновится.</span>}
            {error && <span style={{ color: 'var(--wine-bright)', fontSize: 12 }} data-testid="schedule-error">{error}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
